import React from "react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { useLang } from "../context/LanguageContext";

export default function Footer() {
  const { settings } = useSiteSettings() || {};
  const { lang, t, tBrand, tCity } = useLang();
  const g = settings?.general || {};
  const f = settings?.footer || {};

  const siteName = tBrand(g.site_name || "SHAMSTRADE");

  // Pick translated about/tagline if admin hasn't customized in this lang
  const pick = (baseKey, arKey, fallbackKey) => {
    if (lang === "ar") return f[arKey] || f[baseKey] || t(fallbackKey);
    return f[baseKey] || t(fallbackKey);
  };
  const aboutText = pick("about", "about_ar", "footer.about");
  const taglineRight = pick("tagline_right", "tagline_right_ar", "footer.tagline");
  const copyright =
    (lang === "ar" ? f.copyright_text_ar : f.copyright_text) ||
    `© ${new Date().getFullYear()} ${siteName} · ${t("footer.allRights")}`;

  // Operating cities — use AR value if admin typed one, else translate tokens.
  const operatingCitiesRaw =
    (lang === "ar" && f.operating_in_ar) || f.operating_in || "Amman · Irbid · Zarqa";
  const operatingCities = operatingCitiesRaw
    .split(/[\s·•,/]+/)
    .map((c) => (lang === "ar" ? tCity(c.trim()) : c.trim()))
    .filter(Boolean)
    .join(" · ");

  return (
    <footer data-testid="site-footer" className="bg-ink text-white mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3">
            {g.logo_url && (
              <img src={g.logo_url} alt={siteName} className="w-12 h-12 object-contain bg-white rounded p-1" />
            )}
            <div className="font-display font-black text-3xl tracking-tight">{siteName}</div>
          </div>
          <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-md">{aboutText}</p>
          <div className="mt-6 inline-flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em] text-white/60">
            <span className="w-2 h-2 bg-electric block" />
            {t("footer.operating")} · {operatingCities}
          </div>
          {(g.phone || g.email) && (
            <div className="mt-4 text-xs font-mono text-white/60 space-y-1">
              {g.phone && <div>📞 {g.phone}</div>}
              {g.email && <div>✉ {g.email}</div>}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">{t("footer.shop")}</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/shop?type=split" className="hover:text-electric">{t("nav.split")}</Link></li>
            <li><Link to="/shop?type=cassette" className="hover:text-electric">{t("nav.cassette")}</Link></li>
            <li><Link to="/shop?type=floor" className="hover:text-electric">{t("nav.floor")}</Link></li>
            <li><Link to="/shop?type=duct" className="hover:text-electric">{t("nav.duct")}</Link></li>
            <li><Link to="/about" className="hover:text-electric">{t("nav.about")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">{t("footer.account")}</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/login" className="hover:text-electric">{t("footer.signin")}</Link></li>
            <li><Link to="/register" className="hover:text-electric">{t("footer.createAccount")}</Link></li>
            <li><Link to="/account" className="hover:text-electric">{t("footer.myOrders")}</Link></li>
            <li><Link to="/wishlist" className="hover:text-electric">{t("footer.wishlist")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50 font-mono uppercase tracking-widest">
          <span>{copyright}</span>
          <span>{taglineRight}</span>
        </div>
      </div>
    </footer>
  );
}
